import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../../types';
import ReportSubmissionForm from './ReportSubmissionForm';
import MasterLogViewer from './MasterLogViewer';
import ArchiveViewer from './ArchiveViewer';
import DepartmentRoster from './DepartmentRoster';

interface ShipsLogMainProps {
  currentUserProfile: UserProfile | null;
  isAdmin: boolean;
  isDeptLead: boolean;
  events: ClubEvent[];
  users: UserProfile[];
  attendance: Attendance[];
  ranks: RankRequirement[];
}

const ShipsLogMain = React.memo(function ShipsLogMain({ currentUserProfile, isAdmin, isDeptLead, events, users, attendance, ranks }: ShipsLogMainProps) {
  const [activeTab, setActiveTab] = useState<'submission' | 'roster' | 'master' | 'archive'>(isDeptLead ? 'roster' : 'archive');

  return (
    <div className="flex flex-col h-full bg-[var(--background)]">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between border-b-4 border-lcars-blue pb-4 mb-4 gap-4">
        <div>
          <h1 className="text-3xl md:text-5xl font-bold text-lcars-gold uppercase tracking-tighter">Ship's Log</h1>
          <div className="text-lcars-blue text-sm tracking-widest uppercase mt-1">Official Vessel Record & Monthly Reporting</div>
        </div>
        
        {/* Module Navigation */}
        <div className="flex gap-2">
          {(isDeptLead || isAdmin) && (
            <>
              <button
                onClick={() => setActiveTab('roster')}
                className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${
                  activeTab === 'roster' ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)] hover:bg-white/20'
                }`}
              >
                ROSTER & ACTIVITY
              </button>
              <button
                onClick={() => setActiveTab('submission')}
                className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${
                  activeTab === 'submission' ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)] hover:bg-white/20'
                }`}
              >
                REPORTING FLOW
              </button>
            </>
          )}
          <button
            onClick={() => setActiveTab('master')}
            className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${
              activeTab === 'master' ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)] hover:bg-white/20'
            }`}
          >
            MASTER LOG
          </button>
          <button
            onClick={() => setActiveTab('archive')}
            className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${
              activeTab === 'archive' ? 'bg-lcars-blue text-black' : 'bg-white/10 text-[var(--foreground)] hover:bg-white/20'
            }`}
          >
            ARCHIVE
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto min-h-0 relative">
        <AnimatePresence mode="wait">
          {activeTab === 'roster' && (isDeptLead || isAdmin) && (
            <motion.div
              key="roster"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="h-full"
            >
              <DepartmentRoster 
                currentUserProfile={currentUserProfile}
                users={users}
                events={events}
                attendance={attendance}
                ranks={ranks}
              />
            </motion.div>
          )}

          {activeTab === 'submission' && (isDeptLead || isAdmin) && (
            <motion.div
              key="submission"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="h-full"
            >
              <ReportSubmissionForm currentUserProfile={currentUserProfile} isAdmin={isAdmin} />
            </motion.div>
          )}

          {activeTab === 'master' && (
            <motion.div
              key="master"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="h-full"
            >
              <MasterLogViewer isAdmin={isAdmin} events={events} currentUserProfile={currentUserProfile} />
            </motion.div>
          )}

          {activeTab === 'archive' && (
            <motion.div
              key="archive"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="h-full"
            >
              <ArchiveViewer />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

export default ShipsLogMain;
